"use client";

import { useState, useCallback, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input, Textarea } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Icon } from "@/components/ui/icon";
import { Select } from "@/components/ui/select";
import { PICKER_COLORS, type StageItem } from "./types";

export interface StageMappingControl {
  /** Remote ATS stages this local stage can map to. */
  remoteStages: { remote_stage_id: string; name: string }[];
  /** Currently mapped remote stage id (null = unmapped). */
  remoteStageId: string | null;
  /** Display name of the ATS connector (for the field label). */
  connectorName: string;
  onChange: (stageId: string, remoteStageId: string | null) => void;
}

export function CustomStageCard({
  stage,
  displayIndex,
  onUpdate,
  onRemove,
  dragHandleProps,
  atsLinked = false,
  mapping,
}: {
  stage: StageItem;
  displayIndex: number;
  onUpdate: (id: string, patch: Partial<StageItem>) => void;
  onRemove: (id: string) => void;
  dragHandleProps?: React.HTMLAttributes<HTMLButtonElement>;
  /**
   * The job is ATS-linked, so this stage is synced with a remote ATS stage —
   * deletion is disabled (it would break the mapping). The trash control is
   * hidden; users remap via the ATS-stage selector in the edit panel instead.
   */
  atsLinked?: boolean;
  /**
   * When present (ATS-linked, mappable stage), the edit panel shows an
   * "{connector} stage" selector that maps this local stage to a remote ATS
   * stage. Controlled by the parent's mapping draft.
   */
  mapping?: StageMappingControl;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(stage.name);
  const [color, setColor] = useState(stage.color);
  const [description, setDescription] = useState(stage.description);

  // Baseline captured when the edit panel opens so Cancel can restore every
  // field — including the parent-controlled ATS mapping, which mutates via
  // `mapping.onChange` while the panel is open and would otherwise stick.
  const snapshotRef = useRef<{
    name: string;
    color: string;
    description: string;
    remoteStageId: string | null;
  } | null>(null);

  const handleEdit = useCallback(() => {
    snapshotRef.current = {
      name,
      color,
      description,
      remoteStageId: mapping?.remoteStageId ?? null,
    };
    setIsEditing(true);
  }, [name, color, description, mapping?.remoteStageId]);

  const handleSave = useCallback(() => {
    const trimmed = name.trim() || "Untitled Stage";
    onUpdate(stage.id, {
      name: trimmed,
      color,
      description,
      // Preserve the original ATS label once captured — renaming in Arbi
      // must not overwrite what we compare against upstream.
      ...(stage.ats_name ? { ats_name: stage.ats_name } : {}),
    });
    setIsEditing(false);
  }, [stage.id, stage.ats_name, name, color, description, onUpdate]);

  const handleCancel = useCallback(() => {
    const snapshot = snapshotRef.current;
    setName(snapshot?.name ?? stage.name);
    setColor(snapshot?.color ?? stage.color);
    setDescription(snapshot?.description ?? stage.description);
    if (
      mapping &&
      snapshot &&
      mapping.remoteStageId !== snapshot.remoteStageId
    ) {
      mapping.onChange(stage.id, snapshot.remoteStageId);
    }
    setIsEditing(false);
  }, [stage.id, stage.name, stage.color, stage.description, mapping]);

  return (
    <motion.div
      className="bg-surface-header border border-border rounded-lg overflow-hidden shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] z-[1] w-full"
      animate={isEditing ? "open" : "closed"}
      transition={{ duration: 0.4 }}
    >
      <AnimatePresence mode="wait" initial={false}>
        {!isEditing ? (
          <motion.div
            key="view"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
            className="p-4"
          >
            <div className="flex flex-col gap-2.5">
              <div className="flex h-7 items-center justify-between">
                <div className="flex gap-2 items-center">
                  {dragHandleProps && (
                    <button
                      type="button"
                      className="flex items-center justify-center size-7 rounded-md text-contrast/30 hover:text-contrast/60 hover:bg-contrast/6 transition-colors cursor-grab active:cursor-grabbing touch-none"
                      {...dragHandleProps}
                    >
                      <Icon name="IconDotGrid3x3" size={14} />
                    </button>
                  )}
                  <div className="bg-contrast/4 flex items-center justify-center w-7 py-1 px-2 rounded-full shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]">
                    <span className="text-sm font-medium text-heading">
                      {displayIndex}
                    </span>
                  </div>
                  <Badge
                    variant="linear"
                    size="lg"
                    className="rounded-sm"
                    style={
                      { "--gradient-color": stage.color } as React.CSSProperties
                    }
                  >
                    {stage.name}
                  </Badge>
                  {atsLinked &&
                    stage.ats_name &&
                    stage.ats_name.trim() !== stage.name.trim() && (
                      <span className="text-[11px] text-contrast/45 whitespace-nowrap">
                        {stage.ats_name}
                      </span>
                    )}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    aria-label="Edit stage"
                    onClick={handleEdit}
                    className="flex items-center justify-center size-7 rounded-md text-contrast/30 hover:text-heading hover:bg-contrast/6 transition-colors cursor-pointer"
                  >
                    <Icon name="IconEditBig" size={14} />
                  </button>
                  {!atsLinked && (
                    <button
                      type="button"
                      aria-label="Delete stage"
                      onClick={() => onRemove(stage.id)}
                      className="flex items-center justify-center size-7 rounded-md text-contrast/30 hover:text-destructive hover:bg-contrast/6 transition-colors cursor-pointer"
                    >
                      <Icon name="IconTrashCanSimple" size={14} />
                    </button>
                  )}
                </div>
              </div>
              {stage.description && (
                <p className="text-[13px] text-contrast/70 leading-normal">
                  {stage.description}
                </p>
              )}
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="edit"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
            className="p-4 overflow-hidden"
          >
            <div className="flex flex-col gap-3">
              <div>
                <Label className="text-[13px] text-contrast/50 mb-1.5 block">
                  Stage Label
                </Label>
                <Input
                  type="text"
                  variant="default"
                  size="sm"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="First Round Interview"
                  maxLength={50}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Escape") handleCancel();
                  }}
                />
              </div>

              <div>
                <Label className="text-[13px] text-contrast/50 mb-1.5 block">
                  Color
                </Label>
                <div className="flex items-center gap-2">
                  {PICKER_COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setColor(c)}
                      className={`size-4 rounded-full cursor-pointer transition-shadow shrink-0 ${
                        color === c
                          ? "shadow-[0_0_0_2px_var(--surface-header),0_0_0_3.5px_rgba(0,0,0,0.25)] dark:shadow-[0_0_0_2px_var(--surface-header),0_0_0_3.5px_rgba(255,255,255,0.5)]"
                          : "hover:shadow-[0_0_0_2px_var(--surface-header),0_0_0_3.5px_rgba(0,0,0,0.12)] dark:hover:shadow-[0_0_0_2px_var(--surface-header),0_0_0_3.5px_rgba(255,255,255,0.2)]"
                      }`}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-[13px] text-contrast/50 mb-1.5 block">
                  Stage Description
                </Label>
                <Textarea
                  variant="default"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="This stage is for all Profiles who get advanced to the first round interview."
                  rows={4}
                />
              </div>

              {mapping && (
                <div>
                  <Label className="text-[13px] text-contrast/50 mb-1.5 block">
                    {mapping.connectorName} stage
                  </Label>
                  <Select.Root>
                    <Select.Trigger
                      className="w-full"
                      active={!!mapping.remoteStageId}
                    >
                      <Select.TriggerContent>
                        {mapping.remoteStages.find(
                          (rs) => rs.remote_stage_id === mapping.remoteStageId,
                        )?.name ??
                          (mapping.remoteStageId && stage.ats_name
                            ? stage.ats_name
                            : "Map to ATS stage…")}
                      </Select.TriggerContent>
                      <Select.Icon />
                    </Select.Trigger>
                    <Select.Content>
                      <Select.List>
                        <Select.Item
                          selected={!mapping.remoteStageId}
                          onSelect={() => mapping.onChange(stage.id, null)}
                        >
                          <Select.ItemLabel>— Unmapped —</Select.ItemLabel>
                        </Select.Item>
                        {mapping.remoteStages.map((rs) => (
                          <Select.Item
                            key={rs.remote_stage_id}
                            selected={
                              mapping.remoteStageId === rs.remote_stage_id
                            }
                            onSelect={() =>
                              mapping.onChange(stage.id, rs.remote_stage_id)
                            }
                          >
                            <Select.ItemLabel>{rs.name}</Select.ItemLabel>
                            {mapping.remoteStageId === rs.remote_stage_id && (
                              <Select.ItemCheck />
                            )}
                          </Select.Item>
                        ))}
                      </Select.List>
                    </Select.Content>
                  </Select.Root>
                </div>
              )}

              <div className="flex items-center justify-end gap-3">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={handleSave}
                >
                  Save
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
